import { BLANK_PROFILE_PICTURE } from 'constants/defaultConstants';
import { axiosV1 } from 'interceptors/axios';
import { setUser, updateUser } from 'modules/profile/redux/userActions';
import { store } from 'redux/store';
import { User } from 'types';
import { identifyUser } from 'utils/logRocket';
import { decryptResponse } from 'utils/token';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export interface LoginCredentials {
  email: string;
  otp: string;
  session: string;
}

export interface SendOtpCredentials {
  email: string;
}

export interface SendOtpResponse {
  success: boolean;
  message: string;
  session: string;
}

export interface LoginUser {
  id: string;
  email: string;
  name: string;
  sub: string;
  picture?: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  user: LoginUser;
  accessToken: string;
  refreshToken: string;
  idToken: string;
  expiresIn: number;
  isNewUser?: boolean;
  rateLimitInfo?: {
    remainingAttempts: number;
    isBlocked: boolean;
    blockedUntil?: number;
  };
}

export interface RefreshTokenResponse {
  success: boolean;
  accessToken: string;
  expiresIn: number;
}

export interface AuthError {
  message: string;
  code?: string;
}

// Custom authentication API service
export const customAuthApi = {
  // Send OTP to email
  async sendOtp(credentials: SendOtpCredentials): Promise<SendOtpResponse> {
    try {
      console.log('🔐 Sending OTP request to:', `${API_BASE_URL}/otp/send-otp`);
      console.log('🔐 Request payload:', credentials);

      const response = await axiosV1.post(
        `${API_BASE_URL}/otp/send-otp`,
        credentials
      );

      console.log('🔐 OTP response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error(
        '🔐 OTP request failed:',
        error.response?.data || error.message
      );
      throw new Error(error.response?.data?.message || 'Failed to send OTP');
    }
  },

  // Login with email and OTP
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      console.log('🔐 Sending login request to:', `${API_BASE_URL}/otp/login`);
      console.log('🔐 Request payload:', credentials);

      const response = await axiosV1.post(
        `${API_BASE_URL}/otp/login`,
        credentials
      );

      console.log('🔐 Login response:', response.data);
      return response.data;
    } catch (error: any) {
      // Handle rate limiting errors
      if (error.response?.status === 429) {
        const rateLimitError = new Error(
          error.response?.data?.message || 'LoginTooManyFailedAttempts'
        );
        (rateLimitError as any).rateLimitInfo =
          error.response?.data?.rateLimitInfo;
        throw rateLimitError;
      }

      throw new Error(error?.response.data?.error || 'LoginFailed');
    }
  },

  // Refresh access token
  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    try {
      const response = await axiosV1.post(`${API_BASE_URL}/otp/refresh`, {
        refreshToken,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Token refresh failed');
    }
  },

  // Logout
  async logout(accessToken: string): Promise<void> {
    try {
      await axiosV1.post(
        `${API_BASE_URL}/otp/logout`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
    } catch (error: any) {
      // Don't throw error on logout failure
      console.warn('Logout API call failed:', error);
    }
  },

  // Validate token
  async validateToken(accessToken: string): Promise<boolean> {
    try {
      await axiosV1.get(`${API_BASE_URL}/otp/validate`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return true;
    } catch (error: any) {
      return false;
    }
  },

  // Initialize user session (similar to existing initAuthenticatedUser)
  async initializeUser(user: LoginUser): Promise<void> {
    try {
      const requestData = {
        email: user.email,
        authId: user.sub,
        metadata: {
          locale: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      };

      const { data } = await axiosV1.post(`/otp/initialize`, requestData);

      const decodedUserData = (await decryptResponse(data)) as User;

      if (!decodedUserData) return;

      identifyUser(decodedUserData);

      const currentImage = decodedUserData?.image;

      if (currentImage === BLANK_PROFILE_PICTURE && user?.picture) {
        decodedUserData.image = user?.picture;
        const userId = decodedUserData._id;

        store.dispatch(
          updateUser(userId, { image: user?.picture }, userId, false)
        );
      }

      sessionStorage.setItem('sessionInit', 'true');

      window.history.replaceState({}, document.title, window.location.pathname);
      window.location.href = '/#/';

      store.dispatch(setUser(decodedUserData));
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'User initialization failed'
      );
    }
  },
};
